import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { SoftwareInstallationStatusConstants, SoftwareInstallationStatusValue } from '../models/software-installation-status.constants';

/**
 * A single software item inside a drawer accordion category.
 */
export interface DrawerItem {
  /** Unique identifier used as `trackBy` key. */
  id: string;
  /** Human-readable software name, e.g. "Python 3.11". */
  name: string;
  /** Installation status value — one of "installed" | "failed" | "pending". */
  status: string;
  /** Optional error message shown in a tooltip when status is "failed". */
  error: string;
}

/**
 * A named group of {@link DrawerItem}s shown as one accordion panel.
 */
export interface DrawerCategoryGroup {
  /** Category display name, e.g. "SDKs & Frameworks". */
  category: string;
  /** Material icon ligature rendered in the panel header. */
  icon: string;
  /** Ordered list of software items in this category. */
  items: DrawerItem[];
}

/**
 * Data passed from a caller to {@link DrawerService.open} to populate the
 * application-level installation status drawer.
 */
export interface DrawerPayload {
  /** Title text rendered in the drawer header bar. */
  title: string;
  /** Material icon ligature rendered next to the title. */
  titleIcon: string;
  /** Pre-built accordion content — passed straight through to AppDrawerComponent. */
  content: DrawerCategoryGroup[];
}

/**
 * DrawerService — application-level communication bus for the shared installation
 * status drawer ({@link AppDrawerComponent}).
 *
 * Any component that needs to open the drawer injects this service and calls
 * {@link open}. The shell-level `AppDrawerComponent` subscribes to
 * {@link open$} and {@link close$} to drive `MatSidenav` open/close state.
 *
 * @example
 * ```typescript
 * this.drawerService.open({
 *   title: 'Installation Status',
 *   titleIcon: 'verified',
 *   content: this.buildDrawerContent(model),
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class DrawerService {
  private readonly openSubject = new Subject<DrawerPayload>();
  private readonly closeSubject = new Subject<void>();

  /**
   * Emits once each time a caller requests the drawer to open.
   * `AppDrawerComponent` subscribes to this to receive the payload and open `MatSidenav`.
   */
  readonly open$: Observable<DrawerPayload> = this.openSubject.asObservable();

  /**
   * Emits once each time the drawer is requested to close — either by a component
   * calling {@link close} or by the drawer's own close button / backdrop click.
   */
  readonly close$: Observable<void> = this.closeSubject.asObservable();

  /**
   * Opens the application drawer with the provided content.
   * Emits to {@link open$} which is consumed by `AppDrawerComponent`.
   *
   * @param payload - Title, icon, and accordion content to display.
   */
  open(payload: DrawerPayload): void {
    this.openSubject.next(payload);
  }

  /**
   * Requests the application drawer to close.
   * Emits to {@link close$} which is consumed by `AppDrawerComponent`.
   */
  close(): void {
    this.closeSubject.next();
  }

  // ── Drawer accordion helper methods ──────────────────────────────────────

  private readonly statusIconMap: Record<SoftwareInstallationStatusValue, string> = {
    [SoftwareInstallationStatusConstants.INSTALLED]: 'check_circle',
    [SoftwareInstallationStatusConstants.FAILED]: 'cancel',
    [SoftwareInstallationStatusConstants.PENDING]: 'schedule',
  };

  private readonly statusIconClassMap: Record<SoftwareInstallationStatusValue, string> = {
    [SoftwareInstallationStatusConstants.INSTALLED]: 'icon-installed',
    [SoftwareInstallationStatusConstants.FAILED]: 'icon-failed',
    [SoftwareInstallationStatusConstants.PENDING]: 'icon-pending',
  };

  private readonly statusBadgeClassMap: Record<SoftwareInstallationStatusValue, string> = {
    [SoftwareInstallationStatusConstants.INSTALLED]: 'badge-installed',
    [SoftwareInstallationStatusConstants.FAILED]: 'badge-failed',
    [SoftwareInstallationStatusConstants.PENDING]: 'badge-pending',
  };

  /** Returns the Material icon name for the given installation status value. */
  getStatusIcon(status: string): string {
    return this.statusIconMap[status as SoftwareInstallationStatusValue] ?? 'schedule';
  }

  /** Returns the CSS class for the status icon colour. */
  getStatusIconClass(status: string): string {
    return this.statusIconClassMap[status as SoftwareInstallationStatusValue] ?? '';
  }

  /** Returns the CSS class for the status badge pill. */
  getStatusBadgeClass(status: string): string {
    return this.statusBadgeClassMap[status as SoftwareInstallationStatusValue] ?? '';
  }

  /** TrackBy for accordion category groups. */
  trackByCategory(_: number, group: DrawerCategoryGroup): string {
    return group.category;
  }

  /** TrackBy for individual items within a category. */
  trackByItemId(_: number, item: { id: string }): string {
    return item.id;
  }
}
